// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSelector } from "@reduxjs/toolkit";
import {
  buildEdgesForNode,
  buildEquipmentTree,
  getEquipmentHandles,
  getEquipmentGroupHandles,
} from "./equipmentUtils";

import { selectStudios, selectStudioUuid } from "../studio/studioSelector";

import {
  PTZ_CAMERA_EQUIPMENT_TYPE_ID,
  ELEVATION_UNIT_EQUIPMENT_TYPE_ID,
  DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID,
  MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID,
} from "../../utils/sceneEquipmentsConfig";

/* ================= BASE ================= */

const selectEquipmentState = (state) => state.equipment;

// get the selected equipment group to be able to display in the right panel
export const selectEquipmentGroups = createSelector(
  [selectEquipmentState],
  (state) => state.equipmentGroups,
);

// get the top level equipment group where all nested equipment and equipment groups are nested
export const selectFacilityEquipmentGroup = createSelector(
  [selectEquipmentGroups, selectStudioUuid],
  (groups, studioUuid) => {
    if (!studioUuid) {
      return false;
    }

    return groups.find((group) => group.name === studioUuid) ?? null;
  },
);

// get the selected equipment to be able to display its properties and settings in the right panel
export const selectSelectedEquipment = createSelector(
  [selectEquipmentState],
  (state) => state.selectedEquipment,
);

// get the selected equipment group to be able to display in the right panel
export const selectSelectedEquipmentGroup = createSelector(
  [selectEquipmentState],
  (state) => state.selectedEquipmentGroup,
);

// get the camera control group
export const selectCameraControlGroup = createSelector(
  [selectEquipmentState],
  (state) => state.cameraControlGroup,
);

// get presets list
export const selectPresets = createSelector(
  [selectEquipmentState],
  (state) => state.presets,
);

// get preset sequences list
export const selectPresetSequences = createSelector(
  [selectEquipmentState],
  (state) => state.presetSequences,
);

// get the selected preset sequence
export const selectSelectedPresetSequence = createSelector(
  [selectEquipmentState],
  (state) => state.selectedPresetSequence,
);

// get the selected equipment uuid (co_equip_uuid) to send to websocket action
export const selectSelectedEquipmentUuid = createSelector(
  [selectEquipmentState],
  (state) => state.selectedEquipment?.co_equip_uuid,
);

// get the studio uuid by equipment uuid to be able to send websocket action
export const selectStudioUUIDByEquipmentUUID = (state, equipmentUUID) => {
  const studios = selectStudios(state);

  if (!equipmentUUID || !studios?.length) {
    return undefined;
  }

  const studio = studios.find((studio) =>
    studio.studio_equipment?.some(
      (studioEquip) => studioEquip.co_equip_uuid === equipmentUUID,
    ),
  );

  return studio?.studio_uuid;
};

// get equipement inside groups to be able to build edges between equipment inside groups and other equipment or groups
export const selectAllEquipmentInsideGroups = createSelector(
  [selectEquipmentGroups],
  (groups = []) => {
    const groupedEquipment = groups.flatMap((group) => group.equipment ?? []);
    const map = new Map();

    [...groupedEquipment].forEach((eq) => {
      if (eq?.co_equip_uuid) {
        map.set(eq.co_equip_uuid, eq);
      }
    });

    return Array.from(map.values());
  },
);

// get the selected equipment group with its children equipment groups
export const selectSelectedEquipmentGroupWithChildren = createSelector(
  [selectSelectedEquipmentGroup, selectEquipmentGroups],
  (group, groups = []) => {
    if (!group) {
      return null;
    }

    const children = groups.filter(
      (g) => g.parent_uuid === group.equip_group_uuid,
    );

    return {
      ...group,
      children,
    };
  },
);

// get all edges(conection lines) between equipment and groups based on the handles of the equipment and equipment groups
export const selectAllEdges = createSelector(
  [selectAllEquipmentInsideGroups, selectEquipmentGroups],
  (equipmentUsed = [], groups = []) => {
    const equipmentEdges = equipmentUsed.flatMap((eq) =>
      buildEdgesForNode({
        nodeId: eq.co_equip_uuid,
        equipment: equipmentUsed,
        groups,
      }),
    );

    const groupEdges = groups.flatMap((group) =>
      buildEdgesForNode({
        nodeId: group.equip_group_uuid,
        equipment: equipmentUsed,
        groups,
      }),
    );

    const edgeMap = new Map();

    [...equipmentEdges, ...groupEdges].forEach((edge) => {
      edgeMap.set(edge.id, edge);
    });

    return Array.from(edgeMap.values());
  },
);

// get the selected equipment connections
export const selectEquipmentConnections = createSelector(
  [selectSelectedEquipment, selectAllEquipmentInsideGroups],
  (equipment, equipmentList) => {
    const handles = getEquipmentHandles(equipment, equipmentList).filter(
      (h) => !h.decorative,
    );

    const inputs = handles.filter((h) => h.connectionType === "input");
    const outputs = handles.filter((h) => h.connectionType === "output");
    return { inputs, outputs };
  },
);

// get the selected equipment group connections
export const selectSelectedEquipmentGroupConnections = createSelector(
  [
    selectSelectedEquipmentGroup,
    selectAllEquipmentInsideGroups,
    selectEquipmentGroups,
  ],
  (group, equipmentList, groupList) => {
    const handles = getEquipmentGroupHandles(group, equipmentList, groupList);
    const inputs = handles.filter((h) => h.connectionType === "input");
    const outputs = handles.filter((h) => h.connectionType === "output");
    return { inputs, outputs };
  },
);

// get the selected equipment setting for the equipment details
export const selectEquipmentSettings = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config?.settings],
  (settings) => {
    const tree = buildEquipmentTree(settings, "setting");
    return (
      tree?.filter(
        (n) =>
          ![
            "sources",
            "destinations",
            "button_config",
            "joystick_config",
            "active_destination",
            "action_button",
          ].includes(n.name),
      ) || []
    );
  },
);

// get the selected equipment properties for the equipment details
export const selectEquipmentProperties = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config?.properties],
  (properties) => {
    const tree = buildEquipmentTree(properties, "property");
    return (
      tree?.filter(
        (n) =>
          ![
            "streams",
            "inputs",
            "outputs",
            "button_states",
            "actions",
          ].includes(n.name),
      ) || []
    );
  },
);

export const selectEquipmentActions = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config?.properties],
  (properties) => {
    const tree = buildEquipmentTree(properties, "property");
    return (
      tree
        ?.filter((n) => n.name === "actions")?.[0]
        ?.options.filter(
          // Ignore admin actions
          (n) =>
            ![
              "update_connection",
              "confirm_connection",
              "press_button",
            ].includes(n.value),
        ) || []
    );
  },
);

export const selectEquipmentToggles = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config?.settings],
  (settings) => {
    const tree = buildEquipmentTree(settings, "setting");
    const fields =
      tree?.filter((n) => n.name === "destinations")?.[0]?.children ?? [];
    const toggles = [];
    fields?.forEach((f) => {
      const options = f?.children?.filter(
        (n) => n.name === "ctrl_settings",
      )?.[0]?.children;
      options?.forEach((o) => {
        const toggle = { value: o.name, label: o.label };
        if (!toggles.find((t) => t.value === toggle.value)) {
          toggles.push(toggle);
        }
      });
    });
    return toggles;
  },
);

export const selectEquipmentButtonConfig = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config?.settings],
  (settings) => {
    const tree = buildEquipmentTree(settings, "setting");
    return (
      tree
        ?.filter((n) => n.name === "button_config")?.[0]
        ?.children.filter(
          // Ignore brightnesses
          (b) => !["active_brightness", "inactive_brightness"].includes(b.name),
        ) || []
    );
  },
);

// get the selected equipment status
export const selectedEquipmentStatus = createSelector(
  [selectSelectedEquipment],
  (equipment) =>
    equipment?.current_config?.properties?.state?.status?.eprop_val_uuid?.value,
);

// is the equipment group contains at least one PTZ camera to be able to display button in the right panel to open the PTZ control page
export const selectedEquipmentGroupPTZ = createSelector(
  [selectSelectedEquipmentGroup],
  (group) => {
    if (!group) {
      return false;
    }
    return group?.equipment_group_mappings.some(
      (eq) =>
        eq?.company_equipment?.equip_type_uuid === PTZ_CAMERA_EQUIPMENT_TYPE_ID,
    );
  },
);

// get the ptz camera equipment inside the control group
export const selectedPTZ = createSelector(
  [selectCameraControlGroup],
  (group) =>
    group?.equipment?.find(
      (eq) => eq.equip_type_uuid === PTZ_CAMERA_EQUIPMENT_TYPE_ID && eq.direct_member === true,
    ) || null,
);

// get the elevation unit equipment inside the control group
export const selectedElevationUnit = createSelector(
  [selectCameraControlGroup, selectEquipmentGroups],
  (group, equipmentGroups) =>
    group?.equipment?.find(
      (eq) =>
        eq.equip_type_uuid === ELEVATION_UNIT_EQUIPMENT_TYPE_ID ||
        eq.equip_type_uuid === DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID,
    ) ??
    equipmentGroups
      .find((g) => g.equip_group_uuid === group.parent_uuid)
      ?.equipment?.find(
        (eq) =>
          (eq?.equip_type_uuid === ELEVATION_UNIT_EQUIPMENT_TYPE_ID ||
            eq?.equip_type_uuid === DUAL_STACK_ELEVATION_UNIT_EQUIPMENT_TYPE_ID) && eq?.direct_member === true,
      ) ??
    null,
);

// get the morail carriage equipment inside the control group
export const selectedMorailCarriage = createSelector(
  [selectCameraControlGroup, selectEquipmentGroups],
  (group, equipmentGroups) =>
    group?.equipment?.find(
      (eq) => eq.equip_type_uuid === MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID,
    ) ??
    equipmentGroups
      .find((g) => g.equip_group_uuid === group.parent_uuid)
      ?.equipment?.find(
        (eq) => eq?.equip_type_uuid === MORAIL_CARRIAGE_EQUIPMENT_TYPE_ID && eq?.direct_member === true,
      ) ??
    null,
);

// get the equipment inside the camera control group to be able to display them in the camera control group navigation
export const selectCameraControlGroups = createSelector(
  [selectSelectedEquipmentGroupWithChildren, selectEquipmentGroups],
  (equipmentGroup, equipmentGroups) => {
    if (!equipmentGroup) {
      return [];
    }

    const hasPTZ = (equip) =>
      equip?.equip_type_uuid === PTZ_CAMERA_EQUIPMENT_TYPE_ID &&
      equip?.direct_member === true;

    const collectGroupsWithPTZ = (group) => {
      const result = [];

      if (group?.equipment?.some(hasPTZ)) {
        result.push(group);
      }

      if (group?.children?.length) {
        const childEquipmentGroups = equipmentGroups.filter(
          (g) => g.parent_uuid === group.equip_group_uuid,
        );

        childEquipmentGroups.forEach((child) => {
          result.push(...collectGroupsWithPTZ(child));
        });
      }

      return [...result].sort((a, b) => a.name.localeCompare(b.name));
    };

    return collectGroupsWithPTZ(equipmentGroup);
  },
);

export const selectEquipmentStreams = createSelector(
  [(state) => state.equipment.selectedEquipment?.current_config],
  (config) =>
    Object.keys(config?.settings?.sources)
      .filter((v) => v.startsWith("stream"))
      .sort()
      .map(
        (s) =>
          s && {
            name: s,
            uuid: config?.settings?.sources?.[s]?.uuid?.equip_setting_val_uuid
              ?.value,
            properties: config?.properties?.state?.[s],
          },
      ),
);
